import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const getCountryFlag = (countryCode) => {
  if (!/^[A-Z]{2}$/.test(countryCode)) return "";

  return countryCode
    .split("")
    .map((letter) => String.fromCodePoint(letter.charCodeAt(0) + 127397))
    .join("");
};

const OtherPersonModal = ({
  open,
  onClose,
  otherPersonName,
  handleOtherPersonNameChange,
  otherPersonPhone,
  handleOtherPersonPhoneChange,
  otherPersonPhoneCountry,
  phoneCountries,
  handleOtherPersonPhoneCountryChange,
  handleAcceptOtherPerson,
  invalidField,
  invalidFields,
}) => {
  const [isCountrySelectOpen, setIsCountrySelectOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const filteredPhoneCountries = useMemo(() => {
    const searchValue = countrySearch.trim().toLowerCase();

    if (!searchValue) return phoneCountries;

    return phoneCountries.filter(
      (country) =>
        country.name.toLowerCase().includes(searchValue) ||
        country.dial_code.includes(searchValue),
    );
  }, [countrySearch, phoneCountries]);
  const closeCountrySelect = () => {
    setIsCountrySelectOpen(false);
    setCountrySearch("");
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="other-person-modal-title"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: 24,
          p: 2,
          width: {
            xs: "90%",
            sm: "420px",
          },
        }}
      >
        <Typography
          id="other-person-modal-title"
          variant="h6"
          component="h2"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          Datos de otra persona
        </Typography>
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Nombre y Apellidos"
            name="otherPersonName"
            type="text"
            value={otherPersonName}
            onChange={handleOtherPersonNameChange}
            error={invalidField === invalidFields.otherPersonName}
            inputProps={{ style: { textTransform: "uppercase" } }}
          />
          <TextField
            fullWidth
            label="Telefono"
            name="otherPersonPhone"
            type="tel"
            value={otherPersonPhone}
            onChange={handleOtherPersonPhoneChange}
            error={invalidField === invalidFields.otherPersonPhone}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Select
                    variant="standard"
                    value={otherPersonPhoneCountry}
                    open={isCountrySelectOpen}
                    onOpen={() => setIsCountrySelectOpen(true)}
                    onClose={closeCountrySelect}
                    onChange={(event) => {
                      handleOtherPersonPhoneCountryChange(event);
                      closeCountrySelect();
                    }}
                    disableUnderline
                    renderValue={(value) => {
                      const country = phoneCountries.find(
                        (item) => item.code === value,
                      );
                      if (!country) return "+51";

                      return `${getCountryFlag(country.code)} ${
                        country.dial_code
                      }`;
                    }}
                    MenuProps={{
                      autoFocus: false,
                      PaperProps: {
                        sx: {
                          maxHeight: 320,
                          width: 280,
                        },
                      },
                    }}
                    sx={{ minWidth: 78, fontSize: 14 }}
                  >
                    <ListSubheader
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        py: 1,
                      }}
                    >
                      <TextField
                        autoFocus
                        size="small"
                        placeholder="Buscar"
                        value={countrySearch}
                        onChange={(event) =>
                          setCountrySearch(event.target.value)
                        }
                        onKeyDown={(event) => event.stopPropagation()}
                        fullWidth
                      />
                      <IconButton
                        size="small"
                        onClick={closeCountrySelect}
                        aria-label="Cerrar"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </ListSubheader>
                    {phoneCountries.length === 0 && (
                      <MenuItem value={otherPersonPhoneCountry}>
                        {getCountryFlag(otherPersonPhoneCountry)} +51
                      </MenuItem>
                    )}
                    {filteredPhoneCountries.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {getCountryFlag(country.code)} {country.dial_code}{" "}
                        {country.name}
                      </MenuItem>
                    ))}
                    {phoneCountries.length > 0 &&
                      filteredPhoneCountries.length === 0 && (
                        <MenuItem disabled>No hay resultados</MenuItem>
                      )}
                  </Select>
                </InputAdornment>
              ),
            }}
            inputProps={{ maxLength: 25 }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              pt: 1,
            }}
          >
            <Button variant="contained" color="error" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAcceptOtherPerson}
            >
              Aceptar
            </Button>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};

OtherPersonModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  otherPersonName: PropTypes.string.isRequired,
  handleOtherPersonNameChange: PropTypes.func.isRequired,
  otherPersonPhone: PropTypes.string.isRequired,
  handleOtherPersonPhoneChange: PropTypes.func.isRequired,
  otherPersonPhoneCountry: PropTypes.string.isRequired,
  phoneCountries: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      dial_code: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
    }),
  ).isRequired,
  handleOtherPersonPhoneCountryChange: PropTypes.func.isRequired,
  handleAcceptOtherPerson: PropTypes.func.isRequired,
  invalidField: PropTypes.string.isRequired,
  invalidFields: PropTypes.object.isRequired,
};

export default OtherPersonModal;
